import React, { useState, useEffect } from 'react';
import { generateXmlFromDisclosure, validateDisclosureData } from '../utils/XmlUtils';
import { getCustomers } from '../services/apiCustomers.ts';
import { getVendors } from '../services/IngredientsService';
import azureStorageService from '../services/azureStorageService';
import './XmlGenerator.css';

const MultiWellXmlGenerator = ({ selectedWells, wellIngredients, waterSources, onBack }) => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [wellXmlData, setWellXmlData] = useState({}); // Store XML content for each well
  const [wellErrors, setWellErrors] = useState({}); // Store errors for each well
  const [wellSuccess, setWellSuccess] = useState({}); // Store success messages for each well
  const [activeWellIndex, setActiveWellIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch customers and vendors on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [customersData] = await Promise.all([
          getCustomers(),
          getVendors()
        ]);
        setCustomers(customersData);
        console.log('Customers data:', customersData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setWellErrors(prev => ({
          ...prev,
          general: 'Failed to fetch customer or vendor data. Some information may not be displayed correctly.'
        }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-detect customer associations from selected wells
  useEffect(() => {
    if (customers.length > 0 && selectedWells.length > 0) {
      // Get all unique customer_ids from the selected wells
      const customerIds = selectedWells
        .map(well => well.customer_id)
        .filter(id => id !== null && id !== undefined);
      
      const uniqueCustomerIds = [...new Set(customerIds)];
      
      console.log('Customer IDs found in wells:', customerIds);
      console.log('Unique customer IDs:', uniqueCustomerIds);
      
      if (uniqueCustomerIds.length === 1) {
        // All wells have the same customer_id
        const customerId = uniqueCustomerIds[0];
        const matchedCustomer = customers.find(c => c.id === customerId);
        
        if (matchedCustomer) {
          setSelectedCustomerId(customerId.toString());
          console.log(`Auto-selected customer: ${matchedCustomer.name} (ID: ${customerId}) - All wells associated`);
          setWellErrors(prev => ({
            ...prev,
            general: ''
          }));
        } else {
          console.warn(`Customer ID ${customerId} not found in customers list`);
          setWellErrors(prev => ({
            ...prev,
            general: `Associated customer (ID: ${customerId}) not found. Please select a customer manually.`
          }));
        }
      } else if (uniqueCustomerIds.length > 1) {
        // Wells have different customer associations
        console.log('Wells have different customer associations:', uniqueCustomerIds);
        setWellErrors(prev => ({
          ...prev,
          general: 'Selected wells have different customer associations. Please select a customer manually for XML generation.'
        }));
      } else if (customerIds.length > 0 && customerIds.length < selectedWells.length) {
        // Some wells have customer_id, others don't
        console.log('Mixed customer associations - some wells have customer_id, others do not');
        setWellErrors(prev => ({
          ...prev,
          general: 'Some wells have customer associations while others do not. Please select a customer manually.'
        }));
      }
      // If no customer_ids found, just leave the dropdown empty (normal flow)
    }
  }, [customers, selectedWells]);

  // Generate XML for a specific well
  const handleGenerateXmlForWell = async (well) => {
    const wellName = well.wellName || well.name;
    const ingredients = wellIngredients[wellName] || [];

    try {
      
      // Ensure chemical data is properly formatted
      const sanitizedChemicalData = ingredients.map(chemical => ({
        ...chemical,
        // Ensure supplier is a string
        supplier: typeof chemical.supplier === 'string' ? chemical.supplier : 
                (chemical.supplier ? String(chemical.supplier) : ''),
        // Ensure other string fields are strings
        tradeName: typeof chemical.tradeName === 'string' ? chemical.tradeName : 
                  (chemical.tradeName ? String(chemical.tradeName) : ''),
        purpose: typeof chemical.purpose === 'string' ? chemical.purpose : 
                (chemical.purpose ? String(chemical.purpose) : ''),
        ingredientName: typeof chemical.ingredientName === 'string' ? chemical.ingredientName : 
                      (chemical.ingredientName ? String(chemical.ingredientName) : ''),
        casNumber: typeof chemical.casNumber === 'string' ? chemical.casNumber : 
                  (chemical.casNumber ? String(chemical.casNumber) : ''),
        comments: typeof chemical.comments === 'string' ? chemical.comments : 
                (chemical.comments ? String(chemical.comments) : '')
      }));
      
      // Create a copy of wellData to add the operator number if needed
      const wellDataWithOperator = { ...well };
      
      // FIXED: Always ensure waterSources is properly set
      // Use waterSources prop if available, otherwise use wellData.waterSource
      if (waterSources) {
        wellDataWithOperator.waterSource = waterSources;
      } else if (well.waterSource) {
        wellDataWithOperator.waterSource = well.waterSource;
      }
      
      console.log(`${wellName} - Final wellDataWithOperator.waterSource:`, wellDataWithOperator.waterSource);
      
      // Check if wellData has a customer field that matches a customer name
      if (well.customer && customers.length > 0) {
        console.log(well)
        console.log(`Looking for customer: ${well.customer}`);
        console.log(`customers: ${customers}`);
        const matchedCustomer = customers.find(c => c.id === well.customer_id);
        
        if (matchedCustomer) {
          console.log(`Found matching customer: ${matchedCustomer.name} with operator number: ${matchedCustomer.operator_number}`);
          wellDataWithOperator.operatorNumber = matchedCustomer.operator_number;
        } else {
          console.log(`No matching customer found for: ${well.customer}`);
        }
      } else {
        console.log('No customer information available or customers not loaded yet');
      }
      
      // Validate data before generating XML
      const validation = validateDisclosureData(wellDataWithOperator, sanitizedChemicalData);
      
      if (!validation.success) {
        setWellErrors(prev => ({
          ...prev,
          [wellName]: validation.error
        }));
        setWellSuccess(prev => ({
          ...prev,
          [wellName]: ''
        }));
        return;
      }
      
      // Check if any proprietary chemicals don't have vendor information
      const proprietaryWithoutVendor = sanitizedChemicalData.filter(
        chem => chem.casNumber === 'Proprietary' && !chem.vendorId
      );
      
      if (proprietaryWithoutVendor.length > 0) {
        setWellErrors(prev => ({
          ...prev,
          [wellName]: 'Some proprietary chemicals do not have vendor information. Please add vendor information in the Ingredients Management section.'
        }));
        setWellSuccess(prev => ({
          ...prev,
          [wellName]: ''
        }));
        return;
      }
      
      // Generate XML
      const xml = await generateXmlFromDisclosure(wellDataWithOperator, sanitizedChemicalData);
      setWellXmlData(prev => ({
        ...prev,
        [wellName]: xml
      }));
      setWellSuccess(prev => ({
        ...prev,
        [wellName]: 'XML generated successfully. You can now download the file.'
      }));
      setWellErrors(prev => ({
        ...prev,
        [wellName]: ''
      }));
    } catch (error) {
      console.error(`Error generating XML for ${wellName}:`, error);
      setWellErrors(prev => ({
        ...prev,
        [wellName]: `Failed to generate XML: ${error.message}`
      }));
      setWellSuccess(prev => ({
        ...prev,
        [wellName]: ''
      }));
    }
  };

  // Generate XML for all wells
  const handleGenerateAllXml = async () => {
    setIsLoading(true);
    try {
      for (const well of selectedWells) {
        await handleGenerateXmlForWell(well);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Download XML for a specific well
  const handleDownloadXml = (wellName) => {
    const xmlContent = wellXmlData[wellName];
    if (!xmlContent) {
      setWellErrors(prev => ({
        ...prev,
        [wellName]: 'No XML content to download. Please generate XML first.'
      }));
      return;
    }

    try {
      // Create a blob with the XML content
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      
      // Create a download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${wellName} Chemical Disclosure.xml`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setWellSuccess(prev => ({
        ...prev,
        [wellName]: `XML file "${wellName} Chemical Disclosure.xml" has been downloaded.`
      }));
    } catch (error) {
      console.error(`Error downloading XML for ${wellName}:`, error);
      setWellErrors(prev => ({
        ...prev,
        [wellName]: `Failed to download XML: ${error.message}`
      }));
    }
  };

  // Download all XMLs as a zip file (simplified - individual downloads for now)
  const handleDownloadAllXml = () => {
    selectedWells.forEach(well => {
      const wellName = well.wellName || well.name;
      if (wellXmlData[wellName]) {
        handleDownloadXml(wellName);
      }
    });
  };

  // Save XML for a specific well to Azure
  const handleSaveToAzure = async (wellName) => {
    const xmlContent = wellXmlData[wellName];
    if (!xmlContent) {
      setWellErrors(prev => ({
        ...prev,
        [wellName]: 'No XML content to save. Please generate XML first.'
      }));
      return;
    }

    // Validate customer selection
    if (!selectedCustomerId) {
      setWellErrors(prev => ({
        ...prev,
        [wellName]: 'Please select a customer before saving the XML file.'
      }));
      return;
    }

    try {
      setIsSaving(true);
      
      // Find the selected customer
      const selectedCustomer = customers.find(c => c.id.toString() === selectedCustomerId);
      if (!selectedCustomer) {
        throw new Error('Selected customer not found');
      }
      
      // Find the well data
      const well = selectedWells.find(w => (w.wellName || w.name) === wellName);
      if (!well) {
        throw new Error('Well data not found');
      }
      
      // Create metadata from well data
      const metadata = {
        wellName: wellName,
        operator: well.customer || well.operatorName || 'Unknown Operator',
        apiNumber: well.apiNumber || '',
        state: well.state || '',
        county: well.county || '',
        date: new Date().toISOString(),
        customerId: selectedCustomerId,
        customerName: selectedCustomer.name
      };
      
      // Save the XML file to Azure Blob Storage
      const result = await azureStorageService.saveXmlFile(
        `${wellName} Chemical Disclosure.xml`, 
        xmlContent,
        metadata
      );
      
      if (result.success) {
        setWellSuccess(prev => ({
          ...prev,
          [wellName]: `XML file "${wellName} Chemical Disclosure.xml" has been saved to cloud storage.`
        }));
      } else {
        throw new Error('Failed to save file to cloud storage');
      }
    } catch (error) {
      console.error(`Error saving XML for ${wellName} to Azure:`, error);
      
      // Check if it's a connection string error
      if (error.message.includes('Azure Storage is not initialized')) {
        setWellErrors(prev => ({
          ...prev,
          [wellName]: 'Azure Storage is not configured. Please add your Azure Storage connection string to the .env file.'
        }));
      } else {
        setWellErrors(prev => ({
          ...prev,
          [wellName]: `Failed to save XML to cloud storage: ${error.message}`
        }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Save all XMLs to Azure
  const handleSaveAllToAzure = async () => {
    if (!selectedCustomerId) {
      setWellErrors(prev => ({
        ...prev,
        general: 'Please select a customer before saving XML files.'
      }));
      return;
    }

    setIsSaving(true);
    try {
      for (const well of selectedWells) {
        const wellName = well.wellName || well.name;
        if (wellXmlData[wellName]) {
          await handleSaveToAzure(wellName);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  // View XML in browser for a specific well
  const handleViewInBrowser = (wellName) => {
    const xmlContent = wellXmlData[wellName];
    if (!xmlContent) {
      setWellErrors(prev => ({
        ...prev,
        [wellName]: 'No XML content to view. Please generate XML first.'
      }));
      return;
    }

    try {
      // Create a blob with the XML content
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Open in a new tab
      window.open(url, '_blank');
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error(`Error viewing XML for ${wellName}:`, error);
      setWellErrors(prev => ({
        ...prev,
        [wellName]: `Failed to view XML: ${error.message}`
      }));
    }
  };

  const activeWell = selectedWells[activeWellIndex];
  const activeWellName = activeWell?.wellName || activeWell?.name;
  const hasGeneratedXml = Object.keys(wellXmlData).length > 0;
  const allWellsHaveXml = selectedWells.every(well => {
    const wellName = well.wellName || well.name;
    return wellXmlData[wellName];
  });

  return (
    <div className="xml-generator">
      <div className="xml-generator-header">
        <h2>XML Generator - Multiple Wells</h2>
        <p>Generate and download XML files for FracFocus submission for each well</p>
      </div>
      
      {wellErrors.general && <div className="error-message">{wellErrors.general}</div>}
      
      <div className="xml-generator-content">
        <div className="customer-selection">
          {selectedCustomerId ? (
            <div className="customer-info">
              <label>Associated Customer:</label>
              <div className="customer-display">
                <span className="customer-name">
                  {customers.find(c => c.id.toString() === selectedCustomerId)?.name || 'Unknown Customer'}
                </span>
                <span className="customer-id">(ID: {selectedCustomerId})</span>
              </div>
              <div className="customer-status">
                {(() => {
                  const customerIds = selectedWells
                    .map(well => well.customer_id)
                    .filter(id => id !== null && id !== undefined);
                  const uniqueCustomerIds = [...new Set(customerIds)];
                  
                  if (uniqueCustomerIds.length === 1 && customerIds.length === selectedWells.length) {
                    return <span className="status-success">✓ All wells associated with this customer</span>;
                  } else if (uniqueCustomerIds.length > 1) {
                    return <span className="status-warning">⚠ Wells have different customer associations</span>;
                  } else if (customerIds.length > 0 && customerIds.length < selectedWells.length) {
                    return <span className="status-warning">⚠ Mixed customer associations</span>;
                  } else {
                    return <span className="status-info">ℹ Customer manually selected</span>;
                  }
                })()}
              </div>
              <div className="customer-change">
                <button 
                  type="button" 
                  className="change-customer-btn"
                  onClick={() => setSelectedCustomerId('')}
                >
                  Change Customer
                </button>
              </div>
            </div>
          ) : (
            <div className="customer-selection-dropdown">
              <label htmlFor="customerSelect">Associate with Customer (required for saving):</label>
              <select
                id="customerSelect"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="customer-select"
                required
              >
                <option value="">-- Select a Customer --</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id.toString()}>
                    {customer.name}
                  </option>
                ))}
              </select>
              {!selectedCustomerId && hasGeneratedXml && (
                <div className="customer-warning">
                  You must select a customer before saving XML files.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Well Tabs */}
        <div className="well-tabs">
          {selectedWells.map((well, index) => {
            const wellName = well.wellName || well.name;
            const hasXml = !!wellXmlData[wellName];
            const hasError = !!wellErrors[wellName];
            return (
              <button
                key={wellName}
                className={`well-tab ${index === activeWellIndex ? 'active' : ''} ${hasXml ? 'has-xml' : ''} ${hasError ? 'has-error' : ''}`}
                onClick={() => setActiveWellIndex(index)}
              >
                {wellName}
                {hasXml ? ' ✓' : hasError ? ' ⚠' : ''}
              </button>
            );
          })}
        </div>

        {/* Active Well Content */}
        {activeWell && (
          <div className="active-well-content">
            <div className="well-summary">
              <h3>{activeWellName}</h3>
              <div className="well-summary-grid">
                <div className="summary-item">
                  <span className="summary-label">API Number:</span>
                  <span className="summary-value">{activeWell.apiNumber}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Start Date:</span>
                  <span className="summary-value">{new Date(activeWell.startDate).toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">End Date:</span>
                  <span className="summary-value">{new Date(activeWell.endDate).toLocaleString()}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">State:</span>
                  <span className="summary-value">{activeWell.state}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">County:</span>
                  <span className="summary-value">{activeWell.county}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">TVD:</span>
                  <span className="summary-value">{activeWell.tvd} ft</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total Clean:</span>
                  <span className="summary-value">{activeWell.totalClean} gal</span>
                </div>
              </div>
            </div>

            <div className="chemical-summary">
              <h4>Chemical Summary for {activeWellName}</h4>
              <p>Total Chemicals: {wellIngredients[activeWellName]?.length || 0}</p>
              <p>MSDS Components: {wellIngredients[activeWellName]?.filter(chem => chem.comments !== 'Non-MSDS Component').length || 0}</p>
              <p>Non-MSDS Components: {wellIngredients[activeWellName]?.filter(chem => chem.comments === 'Non-MSDS Component').length || 0}</p>
            </div>

            {wellErrors[activeWellName] && (
              <div className="error-message">{wellErrors[activeWellName]}</div>
            )}
            
            {wellSuccess[activeWellName] && (
              <div className="success-message">{wellSuccess[activeWellName]}</div>
            )}

            {wellXmlData[activeWellName] && (
              <div className="xml-preview">
                <h4>XML Preview for {activeWellName}</h4>
                <pre className="xml-content">{wellXmlData[activeWellName]}</pre>
              </div>
            )}

            <div className="well-actions">
              <button 
                className="generate-button" 
                onClick={() => handleGenerateXmlForWell(activeWell)}
                disabled={isLoading}
              >
                {isLoading ? 'Generating...' : `Generate XML for ${activeWellName}`}
              </button>
              
              {wellXmlData[activeWellName] && (
                <>
                  <button 
                    className="download-button" 
                    onClick={() => handleDownloadXml(activeWellName)}
                  >
                    Download XML
                  </button>
                  
                  <button 
                    className="save-button" 
                    onClick={() => handleSaveToAzure(activeWellName)}
                    disabled={isSaving || !selectedCustomerId}
                  >
                    {isSaving ? 'Saving...' : 'Save to Cloud'}
                  </button>
                  
                  <button 
                    className="view-button" 
                    onClick={() => handleViewInBrowser(activeWellName)}
                  >
                    View in Browser
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        <div className="bulk-actions">
          <h4>Bulk Actions</h4>
          <div className="bulk-actions-buttons">
            <button 
              className="generate-all-button" 
              onClick={handleGenerateAllXml}
              disabled={isLoading}
            >
              {isLoading ? 'Generating All...' : `Generate XML for All Wells (${selectedWells.length})`}
            </button>
            
            {hasGeneratedXml && (
              <>
                <button 
                  className="download-all-button" 
                  onClick={handleDownloadAllXml}
                  disabled={!allWellsHaveXml}
                >
                  Download All XMLs ({Object.keys(wellXmlData).length}/{selectedWells.length})
                </button>
                
                <button 
                  className="save-all-button" 
                  onClick={handleSaveAllToAzure}
                  disabled={isSaving || !selectedCustomerId || !allWellsHaveXml}
                >
                  {isSaving ? 'Saving All...' : `Save All to Cloud (${Object.keys(wellXmlData).length}/${selectedWells.length})`}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="xml-generator-actions">
          <button className="back-button" onClick={onBack}>
            Back to Chemical Disclosure
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiWellXmlGenerator;
